import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Trash2, Users, LayoutGrid, ArrowLeft, Pencil, Download, Loader2, Star } from "lucide-react";
import api from "@/lib/api";
import { EditBusinessModal } from "@/components/EditBusinessModal";

const EASE = [0.16, 1, 0.3, 1];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [activeTab, setActiveTab] = useState("registrations");
  const [referrals, setReferrals] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState({ title: "", event_date: "", location: "", description: "" });
  const [eventSaving, setEventSaving] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);

  const inputCls =
    "w-full rounded-xl border border-stone_warm bg-white px-4 py-3 text-sm text-charcoal placeholder-charcoal/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20";

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => {
        setAdmin(res.data);
        return api.get("/admin/businesses");
      })
      .then((res) => {
        setBusinesses(res.data);
        return api.get("/settings/approval");
      })
      .then((res) => {
        setApprovalRequired(res.data.approval_required);
        return Promise.all([api.get("/admin/referrals"), api.get("/events"), api.get("/admin/testimonials")]);
      })
      .then(([refs, evs, tsts]) => {
        setReferrals(refs.data);
        setEvents(evs.data);
        setTestimonials(tsts.data);
      })
      .catch(() => navigate("/admin/login"))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setEventSaving(true);
    try {
      if (editingEventId) {
        const res = await api.put(`/events/${editingEventId}`, eventForm);
        setEvents((list) => list.map((x) => (x.id === editingEventId ? res.data : x)).sort((a, b) => a.event_date.localeCompare(b.event_date)));
        toast.success("Gathering updated.");
        setEditingEventId(null);
      } else {
        const res = await api.post("/events", eventForm);
        setEvents((list) => [...list, res.data].sort((a, b) => a.event_date.localeCompare(b.event_date)));
        toast.success("Gathering added to the events board — owners have been emailed.");
      }
      setEventForm({ title: "", event_date: "", location: "", description: "" });
    } catch {
      toast.error("Could not save the event.");
    } finally {
      setEventSaving(false);
    }
  };

  const startEditEvent = (ev) => {
    setEditingEventId(ev.id);
    setEventForm({ title: ev.title, event_date: ev.event_date, location: ev.location || "", description: ev.description });
    window.__lenis?.scrollTo(0, { duration: 1 });
  };

  const handleDeleteTestimonial = async (t) => {
    if (!window.confirm("Remove this endorsement?")) return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      setTestimonials((list) => list.filter((x) => x.id !== t.id));
      toast.success("Endorsement removed.");
    } catch {
      toast.error("Could not remove the endorsement.");
    }
  };

  const handleToggleFeatured = async (b) => {
    const next = !b.featured;
    try {
      await api.put(`/admin/businesses/${b.id}/featured`, { featured: next });
      setBusinesses((list) => list.map((x) => (x.id === b.id ? { ...x, featured: next } : x)));
      toast.success(next ? `${b.business_name} is now featured.` : `${b.business_name} is no longer featured.`);
    } catch {
      toast.error("Could not update the listing.");
    }
  };

  const handleSaveReferralNote = async (r) => {
    const el = document.getElementById(`note-${r.id}`);
    const notes = el ? el.value : "";
    try {
      await api.put(`/admin/referrals/${r.id}/notes`, { notes });
      setReferrals((list) => list.map((x) => (x.id === r.id ? { ...x, notes } : x)));
      toast.success("Note saved.");
    } catch {
      toast.error("Could not save the note.");
    }
  };

  const handleSetReferralStatus = async (r, status) => {
    try {
      await api.put(`/admin/referrals/${r.id}/status`, { status });
      setReferrals((list) => list.map((x) => (x.id === r.id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}.`);
    } catch {
      toast.error("Could not update the referral.");
    }
  };

  const handleDeleteEvent = async (ev) => {
    if (!window.confirm(`Remove "${ev.title}" from the events board?`)) return;
    try {
      await api.delete(`/events/${ev.id}`);
      setEvents((list) => list.filter((x) => x.id !== ev.id));
      toast.success("Event removed.");
    } catch {
      toast.error("Could not remove the event.");
    }
  };

  const handleToggleApproval = async () => {
    const next = !approvalRequired;
    setApprovalRequired(next);
    try {
      await api.put("/settings/approval", { approval_required: next });
      toast.success(next
        ? "Approval mode on — new listings stay hidden until you approve them."
        : "Approval mode off — new listings appear instantly.");
    } catch {
      setApprovalRequired(!next);
      toast.error("Could not update the setting.");
    }
  };

  const handleApprove = async (b) => {
    try {
      await api.post(`/admin/businesses/${b.id}/approve`);
      setBusinesses((list) => list.map((x) => (x.id === b.id ? { ...x, approved: true } : x)));
      toast.success(`${b.business_name} is now live in the directory.`);
    } catch {
      toast.error("Could not approve the listing.");
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Remove "${b.business_name}" from the Guild directory?`)) return;
    setDeleting(b.id);
    try {
      await api.delete(`/businesses/${b.id}`);
      setBusinesses((list) => list.filter((x) => x.id !== b.id));
      toast.success(`${b.business_name} removed from the directory.`);
    } catch {
      toast.error("Could not remove the business. Please try again.");
    } finally {
      setDeleting(null);
    }
  };

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch { /* cookie cleared server-side */ }
    navigate("/admin/login");
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/export/registrations.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "joseph-guild-registrations.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export registrations.");
    }
  };

  if (loading || !admin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory" data-testid="admin-loading">
        <p className="font-mono text-xs uppercase tracking-[0.24em] text-charcoal/40">Opening the Guild office…</p>
      </div>
    );
  }

  const sectorCount = new Set(businesses.map((b) => b.sector)).size;

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <header className="border-b border-stone_warm bg-ivory/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-12">
          <div className="flex items-center gap-2.5">
            <span className="inline-block h-2 w-2 rotate-45 bg-gold" aria-hidden="true" />
            <span className="font-serif text-xl font-semibold tracking-tight text-charcoal">Joseph Guild</span>
            <span className="ml-3 rounded-full border border-gold/40 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.22em] text-gold-dark">
              Guild office
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-charcoal/50 transition-colors hover:text-gold-dark sm:flex" data-testid="admin-view-site-link">
              <ArrowLeft size={12} /> View site
            </Link>
            <button
              onClick={handleToggleApproval}
              className="inline-flex items-center gap-2.5 rounded-full border border-charcoal/25 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal transition-colors duration-300 hover:border-gold"
              data-testid="approval-toggle"
              title="When on, new listings stay hidden until you approve them"
            >
              <span className={`relative h-4 w-8 rounded-full transition-colors duration-300 ${approvalRequired ? "bg-gold" : "bg-charcoal/20"}`}>
                <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all duration-300 ${approvalRequired ? "left-[18px]" : "left-0.5"}`} />
              </span>
              Approval {approvalRequired ? "on" : "off"}
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 rounded-full border border-charcoal/25 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark"
              data-testid="admin-export-button"
            >
              <Download size={12} /> Export
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-full border border-charcoal/25 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark"
              data-testid="admin-logout-button"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-14 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
          <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.3em] text-gold-dark">Registrations</p>
          <h1 className="font-serif text-4xl font-light tracking-tighter text-charcoal sm:text-5xl">
            Welcome, <span className="italic text-gold-dark">{admin.name}.</span>
          </h1>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:max-w-xl">
            <div className="border border-stone_warm bg-white p-6" data-testid="stat-total-businesses">
              <div className="flex items-center gap-3 text-charcoal/45">
                <Users size={15} />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em]">Registered businesses</span>
              </div>
              <p className="mt-4 font-serif text-5xl font-light text-charcoal">{businesses.length}</p>
            </div>
            <div className="border border-stone_warm bg-white p-6" data-testid="stat-total-sectors">
              <div className="flex items-center gap-3 text-charcoal/45">
                <LayoutGrid size={15} />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em]">Sectors represented</span>
              </div>
              <p className="mt-4 font-serif text-5xl font-light text-charcoal">{sectorCount}</p>
            </div>
          </div>

          <div className="mt-14 flex flex-wrap gap-2" data-testid="admin-tabs">
            {[
              ["registrations", "Registrations"],
              ["referrals", `Referrals${referrals.length > 0 ? ` (${referrals.length})` : ""}`],
              ["events", "Events"],
              ["testimonials", `Endorsements${testimonials.length > 0 ? ` (${testimonials.length})` : ""}`],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                  activeTab === key
                    ? "border-charcoal bg-charcoal text-ivory"
                    : "border-stone_warm bg-white text-charcoal/60 hover:border-gold hover:text-gold-dark"
                }`}
                data-testid={`admin-tab-${key}`}
              >
                {label}
              </button>
            ))}
          </div>

          {activeTab === "registrations" && (
          <div className="mt-6 overflow-x-auto border border-stone_warm bg-white" data-testid="admin-table-wrapper">
            <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-registrations-table">
              <thead>
                <tr className="border-b border-stone_warm bg-[#F3F0EB]">
                  {["Business", "Owner", "Sector", "Contact", "Location", "Registered", ""].map((h) => (
                    <th key={h} className="px-5 py-4 font-mono text-[10px] font-normal uppercase tracking-[0.2em] text-charcoal/55">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {businesses.map((b) => (
                  <tr key={b.id} className="border-b border-stone_warm/60 transition-colors hover:bg-[#F3F0EB]/50" data-testid={`admin-row-${b.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {b.logo_path ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${b.logo_path}`}
                            alt=""
                            className="h-9 w-9 shrink-0 rounded-full border border-stone_warm object-cover"
                            data-testid={`admin-logo-${b.id}`}
                          />
                        ) : (
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-stone_warm bg-[#F3F0EB]">
                            <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold" aria-hidden="true" />
                          </span>
                        )}
                        <div>
                          <p className="font-serif text-base font-medium text-charcoal">{b.business_name}</p>
                          <p className="mt-0.5 max-w-[220px] truncate text-xs text-charcoal/50">{b.offerings}</p>
                          {!b.approved && (
                            <span className="mt-1.5 inline-block rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-amber-700" data-testid={`pending-badge-${b.id}`}>
                              Pending approval
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-charcoal/70">{b.owner_name}</td>
                    <td className="px-5 py-4">
                      <span className="rounded-full border border-gold/40 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-gold-dark">{b.sector}</span>
                    </td>
                    <td className="px-5 py-4 text-xs text-charcoal/60">
                      <p>{b.email}</p>
                      <p className="mt-1">{b.phone}</p>
                    </td>
                    <td className="px-5 py-4 text-charcoal/60">{b.location}</td>
                    <td className="px-5 py-4 font-mono text-[10px] uppercase tracking-[0.16em] text-charcoal/50">
                      {new Date(b.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleToggleFeatured(b)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                            b.featured
                              ? "border-gold bg-gold text-white"
                              : "border-charcoal/25 text-charcoal hover:border-gold hover:text-gold-dark"
                          }`}
                          data-testid={`feature-business-button-${b.id}`}
                        >
                          <Star size={11} fill={b.featured ? "currentColor" : "none"} /> {b.featured ? "Featured" : "Feature"}
                        </button>
                        {!b.approved && (
                          <button
                            onClick={() => handleApprove(b)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-gold/60 bg-gold/10 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-gold-dark transition-colors duration-300 hover:bg-gold hover:text-white"
                            data-testid={`approve-business-button-${b.id}`}
                          >
                            Approve
                          </button>
                        )}
                        <button
                          onClick={() => setEditing(b)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-charcoal/25 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark"
                          data-testid={`edit-business-button-${b.id}`}
                        >
                          <Pencil size={11} /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(b)}
                          disabled={deleting === b.id}
                          className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-red-700 transition-colors duration-300 hover:bg-red-50 disabled:opacity-50"
                          data-testid={`delete-business-button-${b.id}`}
                        >
                          <Trash2 size={11} /> Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {businesses.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center font-serif text-xl font-light italic text-charcoal/45" data-testid="admin-table-empty">
                      No registrations yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}

          {activeTab === "referrals" && (
            <div className="mt-6 overflow-x-auto border border-stone_warm bg-white" data-testid="admin-referrals-wrapper">
              <table className="w-full min-w-[800px] text-left text-sm" data-testid="admin-referrals-table">
                <thead>
                  <tr className="border-b border-stone_warm bg-[#F3F0EB]">
                    {["Received", "Business", "From", "Contact", "Message", "Status", "Notes"].map((h) => (
                      <th key={h} className="px-5 py-4 font-mono text-[10px] font-normal uppercase tracking-[0.2em] text-charcoal/55">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r) => (
                    <tr key={r.id} className="border-b border-stone_warm/60 transition-colors hover:bg-[#F3F0EB]/50" data-testid={`referral-row-${r.id}`}>
                      <td className="px-5 py-4 font-mono text-[10px] uppercase tracking-[0.16em] text-charcoal/50">
                        {new Date(r.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td className="px-5 py-4 font-serif text-base font-medium text-charcoal">{r.business_name}</td>
                      <td className="px-5 py-4 text-charcoal/70">{r.sender_name}</td>
                      <td className="px-5 py-4 text-xs text-charcoal/60">{r.sender_contact}</td>
                      <td className="max-w-[320px] px-5 py-4 text-sm text-charcoal/70">{r.message}</td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-block rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em] ${
                            (r.status || "new") === "connected"
                              ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                              : (r.status || "new") === "contacted"
                                ? "border-amber-300 bg-amber-50 text-amber-700"
                                : "border-stone_warm bg-white text-charcoal/50"
                          }`}
                          data-testid={`referral-status-${r.id}`}
                        >
                          {r.status || "new"}
                        </span>
                        <div className="mt-2 flex gap-3">
                          {["contacted", "connected"].map((s) => (
                            <button
                              key={s}
                              onClick={() => handleSetReferralStatus(r, s)}
                              className="font-mono text-[9px] uppercase tracking-[0.14em] text-charcoal/45 underline-offset-2 transition-colors hover:text-gold-dark hover:underline"
                              data-testid={`referral-mark-${s}-${r.id}`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <input
                            id={`note-${r.id}`}
                            defaultValue={r.notes || ""}
                            placeholder="Private note…"
                            className="w-40 rounded-lg border border-stone_warm bg-white px-3 py-2 text-xs text-charcoal placeholder-charcoal/35 outline-none transition-colors duration-300 focus:border-gold"
                            data-testid={`referral-note-input-${r.id}`}
                          />
                          <button
                            onClick={() => handleSaveReferralNote(r)}
                            className="font-mono text-[9px] uppercase tracking-[0.14em] text-charcoal/45 underline-offset-2 transition-colors hover:text-gold-dark hover:underline"
                            data-testid={`referral-note-save-${r.id}`}
                          >
                            Save
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {referrals.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-5 py-16 text-center font-serif text-xl font-light italic text-charcoal/45" data-testid="referrals-empty">
                        No referral requests yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "events" && (
            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
              <form onSubmit={handleCreateEvent} className="space-y-5 border border-stone_warm bg-white p-7 lg:col-span-5" data-testid="event-form">
                <div className="flex items-center justify-between gap-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold-dark">
                    {editingEventId ? "Edit gathering" : "Add a gathering"}
                  </p>
                  {editingEventId && (
                    <button
                      type="button"
                      onClick={() => { setEditingEventId(null); setEventForm({ title: "", event_date: "", location: "", description: "" }); }}
                      className="font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal/45 underline-offset-2 transition-colors hover:text-gold-dark hover:underline"
                      data-testid="event-cancel-edit"
                    >
                      Cancel edit
                    </button>
                  )}
                </div>
                <input
                  required
                  value={eventForm.title}
                  onChange={(e) => setEventForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Title — e.g. Guild Breakfast & Networking"
                  className={inputCls}
                  data-testid="event-title-input"
                />
                <input
                  required
                  type="date"
                  value={eventForm.event_date}
                  onChange={(e) => setEventForm((f) => ({ ...f, event_date: e.target.value }))}
                  className={inputCls}
                  data-testid="event-date-input"
                />
                <input
                  value={eventForm.location}
                  onChange={(e) => setEventForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="Location (optional)"
                  className={inputCls}
                  data-testid="event-location-input"
                />
                <textarea
                  required
                  rows={3}
                  value={eventForm.description}
                  onChange={(e) => setEventForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="What is happening, who should come…"
                  className={inputCls}
                  data-testid="event-description-input"
                />
                <button
                  type="submit"
                  disabled={eventSaving}
                  className="inline-flex items-center gap-2 rounded-full bg-charcoal px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ivory transition-colors duration-300 hover:bg-gold disabled:opacity-60"
                  data-testid="event-submit-button"
                >
                  {eventSaving && <Loader2 size={12} className="animate-spin" />}
                  {eventSaving ? "Saving…" : editingEventId ? "Save changes" : "Add to board"}
                </button>
              </form>

              <div className="space-y-4 lg:col-span-7" data-testid="admin-events-list">
                {events.map((ev) => (
                  <div key={ev.id} className="flex items-start justify-between gap-6 border border-stone_warm bg-white p-6" data-testid={`admin-event-${ev.id}`}>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-dark">
                        {new Date(`${ev.event_date}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                        {ev.location ? ` — ${ev.location}` : ""}
                      </p>
                      <p className="mt-2 font-serif text-xl font-medium tracking-tight text-charcoal">{ev.title}</p>
                      <p className="mt-1 text-sm text-charcoal/60">{ev.description}</p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <button
                        onClick={() => startEditEvent(ev)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-charcoal/25 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark"
                        data-testid={`edit-event-button-${ev.id}`}
                      >
                        <Pencil size={11} /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(ev)}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-red-200 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-red-700 transition-colors duration-300 hover:bg-red-50"
                        data-testid={`delete-event-button-${ev.id}`}
                      >
                        <Trash2 size={11} /> Remove
                      </button>
                    </div>
                  </div>
                ))}
                {events.length === 0 && (
                  <p className="border border-dashed border-stone_warm bg-white/60 px-6 py-16 text-center font-serif text-xl font-light italic text-charcoal/45" data-testid="events-empty">
                    No gatherings scheduled yet.
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === "testimonials" && (
            <div className="mt-6 overflow-x-auto border border-stone_warm bg-white" data-testid="admin-testimonials-wrapper">
              <table className="w-full min-w-[800px] text-left text-sm" data-testid="admin-testimonials-table">
                <thead>
                  <tr className="border-b border-stone_warm bg-[#F3F0EB]">
                    {["Received", "Business", "Author", "Endorsement", ""].map((h) => (
                      <th key={h} className="px-5 py-4 font-mono text-[10px] font-normal uppercase tracking-[0.2em] text-charcoal/55">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {testimonials.map((t) => (
                    <tr key={t.id} className="border-b border-stone_warm/60 transition-colors hover:bg-[#F3F0EB]/50" data-testid={`testimonial-row-${t.id}`}>
                      <td className="px-5 py-4 font-mono text-[10px] uppercase tracking-[0.16em] text-charcoal/50">
                        {new Date(t.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td className="px-5 py-4 font-serif text-base font-medium text-charcoal">{t.business_name}</td>
                      <td className="px-5 py-4 text-charcoal/70">
                        {t.author_name}
                        {t.author_business && <p className="mt-0.5 text-xs text-charcoal/50">{t.author_business}</p>}
                      </td>
                      <td className="max-w-[360px] px-5 py-4 text-sm italic text-charcoal/70">“{t.text}”</td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => handleDeleteTestimonial(t)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] text-red-700 transition-colors duration-300 hover:bg-red-50"
                          data-testid={`delete-testimonial-button-${t.id}`}
                        >
                          <Trash2 size={11} /> Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {testimonials.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-16 text-center font-serif text-xl font-light italic text-charcoal/45" data-testid="testimonials-empty-admin">
                        No endorsements yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </main>
      {editing && (
        <EditBusinessModal
          business={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setBusinesses((list) => list.map((x) => (x.id === updated.id ? updated : x)));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}
